import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';
import CalculateIcon from '@mui/icons-material/Calculate';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FactoryIcon from '@mui/icons-material/Factory';
import HomeIcon from '@mui/icons-material/Home';
import PaletteIcon from '@mui/icons-material/Palette';
import PeopleIcon from '@mui/icons-material/People';
import PieChartIcon from '@mui/icons-material/PieChart';
import SettingsIcon from '@mui/icons-material/Settings';
import StoreIcon from '@mui/icons-material/Store';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  selectAccessDealers,
  selectAccessManufacturers,
  selectAccessOrders,
  selectAccessPaintData,
  selectAccessTechnicians,
  selectAccessUsers,
  selectCurrentUser,
} from '../features/user';
import { useAppSelector } from '../store/hooks.ts';
import { UserRole } from '../types';

interface SideMenuProps {
  open: boolean;
  onClose: () => void;
}

const SideMenu = ({ open, onClose }: SideMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const currentUser = useAppSelector(selectCurrentUser);

  const canAccessUsers = useAppSelector(selectAccessUsers);
  const canAccessManufacturers = useAppSelector(selectAccessManufacturers);
  const canAccessDealers = useAppSelector(selectAccessDealers);
  const canAccessTechnicians = useAppSelector(selectAccessTechnicians);
  const canAccessOrders = useAppSelector(selectAccessOrders);
  const canAccessPaintData = useAppSelector(selectAccessPaintData);
  const showManagementSelections =
    canAccessUsers ||
    canAccessManufacturers ||
    canAccessDealers ||
    canAccessTechnicians ||
    canAccessOrders;

  // Reports are accessible to Administrator, Supervisor, CSR (not Dealer)
  const canAccessReports =
    currentUser?.role === UserRole.ADMINISTRATOR ||
    currentUser?.role === UserRole.SUPERVISOR ||
    currentUser?.role === UserRole.CSR;

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 250 }} role="none">
        <List>
          <ListItem disablePadding>
            <ListItemButton selected={currentPath === '/'} onClick={() => handleNavigation('/')}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/order-management'}
              onClick={() => handleNavigation('/order-management')}
            >
              <ListItemIcon>
                <AssignmentIcon />
              </ListItemIcon>
              <ListItemText primary="Orders" />
            </ListItemButton>
          </ListItem>
        </List>
        {showManagementSelections && (
          <>
            <Divider />
            <List>
              {canAccessUsers && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === '/user-management'}
                    onClick={() => handleNavigation('/user-management')}
                  >
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Users" />
                  </ListItemButton>
                </ListItem>
              )}
              {canAccessManufacturers && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === '/manufacturers-management'}
                    onClick={() => handleNavigation('/manufacturers-management')}
                  >
                    <ListItemIcon>
                      <FactoryIcon />
                    </ListItemIcon>
                    <ListItemText primary="Manufacturers" />
                  </ListItemButton>
                </ListItem>
              )}
              {canAccessDealers && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === '/dealer-management'}
                    onClick={() => handleNavigation('/dealer-management')}
                  >
                    <ListItemIcon>
                      <StoreIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dealers" />
                  </ListItemButton>
                </ListItem>
              )}
              {canAccessTechnicians && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === '/technician-management'}
                    onClick={() => handleNavigation('/technician-management')}
                  >
                    <ListItemIcon>
                      <BuildIcon />
                    </ListItemIcon>
                    <ListItemText primary="Technicians" />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
            <Divider />
          </>
        )}
        <Divider />
        <List>
          {/* Orders Due and Orders Past Due are intentionally outside canAccessReports —
              Dealers are authorized to view these reports scoped to their own orders.
              Production feet reports below require canAccessReports (Admin/Supervisor/CSR only). */}
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/reports/orders-due'}
              onClick={() => handleNavigation('/reports/orders-due')}
            >
              <ListItemIcon>
                <DateRangeIcon />
              </ListItemIcon>
              <ListItemText primary="Orders Due" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/reports/orders-past-due'}
              onClick={() => handleNavigation('/reports/orders-past-due')}
            >
              <ListItemIcon>
                <ErrorOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="Orders Past Due" />
            </ListItemButton>
          </ListItem>
          {canAccessReports && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  selected={currentPath === '/reports/products'}
                  onClick={() => handleNavigation('/reports/products')}
                >
                  <ListItemIcon>
                    <AssignmentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Products Report" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={currentPath === '/reports/weekly-feet-per-day'}
                  onClick={() => handleNavigation('/reports/weekly-feet-per-day')}
                >
                  <ListItemIcon>
                    <BarChartIcon />
                  </ListItemIcon>
                  <ListItemText primary="Weekly Feet Per Day" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={currentPath === '/reports/total-feet-per-week'}
                  onClick={() => handleNavigation('/reports/total-feet-per-week')}
                >
                  <ListItemIcon>
                    <PieChartIcon />
                  </ListItemIcon>
                  <ListItemText primary="Total Feet Per Week" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={currentPath === '/reports/total-feet-per-month'}
                  onClick={() => handleNavigation('/reports/total-feet-per-month')}
                >
                  <ListItemIcon>
                    <DonutLargeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Total Feet Per Month" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
        <Divider />
        <List>
          {canAccessPaintData && (
            <ListItem disablePadding>
              <ListItemButton
                selected={currentPath === '/paint-data-management'}
                onClick={() => handleNavigation('/paint-data-management')}
              >
                <ListItemIcon>
                  <PaletteIcon />
                </ListItemIcon>
                <ListItemText primary="Paint Data" />
              </ListItemButton>
            </ListItem>
          )}
          {currentUser?.role !== UserRole.DEALER && (
            <ListItem disablePadding>
              <ListItemButton
                selected={currentPath === '/product-pricing-calculator'}
                onClick={() => handleNavigation('/product-pricing-calculator')}
              >
                <ListItemIcon>
                  <CalculateIcon />
                </ListItemIcon>
                <ListItemText primary="Pricing Calculator" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton
              selected={currentPath === '/settings'}
              onClick={() => handleNavigation('/settings')}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default SideMenu;
